import React, {
  PropTypes as T,
  Component,
} from 'react';
import radium from 'radium';
import { connect } from 'react-redux';

const styles = {
  wrap: {
    padding: 24,
    backgroundColor: '#4a4a4a',
    display: 'block',
    height: 'calc(100vh - 150px)'
  },
  title: {
    textAlign: 'center',
    fontSize: 20,
    color: 'white',
    letterSpacing: 1.5
  },
  video: {
    width: '100%',
    maxHeight: 'calc(100% - 220px)',
  },
  controller: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 42px 0 24px',
    margin: '16px 0 12px 0',
    flexWrap: 'wrap',
  },
  controlBtn: {
    color: '#fff',
    fontSize: 60,
    width: 60,
    backgroundColor: 'transparent',
    border: 0,
    outline: 'none',
    padding: 0,
    margin: '0 38px',
    cursor: 'pointer',
    opacity: 0.75,
    ':hover': {
      opacity: 0.96,
    },
    '@media (max-width: 767px)': {
      width: 30,
      fontSize: 30,
      margin: '10px 15px',
    },
  },
  progressBar: {
    display: 'flex',
    position: 'relative',
    width: 300,
    height: 32,
    padding: '15px 0',
    backgroundColor: '#fff',
    borderRadius: 1,
    backgroundClip: 'content-box',
  },
  progressPan: {
    cursor: 'pointer',
    position: 'absolute',
    top: 0,
    left: 0,
    display: 'block',
    borderRadius: 16,
    width: 32,
    height: 32,
    border: 0,
    outline: 'none',
    backgroundColor: '#FFF',
  },
  movieMode: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '24px 0',
  },
  movieModeToggle: {
    backgroundColor: '#fed312',
    width: 260,
    height: 42,
    borderRadius: 3,
    margin: '0 29px',
    border: 0,
    fontSize: 18,
    lineHeight: '42px',
    color: '#fff',
    fontWeight: 100,
    letterSpacing: 4,
    padding: '0 0 0 4px',
    cursor: 'pointer'
  },
  reverseColor: {
    backgroundColor: '#f87c7f',
  },
};

export const ON = Symbol('ON');
export const OFF = Symbol('OFF');

class SpecificationViewer extends Component {
  constructor(props) {
    super(props);

    this.state = {
      mode: ON,
      playing: false,
      volume: 50,
      muted: false,
      panStartX: null,
      barUnit: 3,
      lastTime: 0,
    };
  }

  componentDidMount() {
    const {
      video,
    } = this.refs;

    video.volume = 0.5;
    this.updateUnitSize();

    window.addEventListener('resize', () => this.updateUnitSize(), false);
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.mode !== this.state.mode) {
      const {
        video,
      } = this.refs;

      video.currentTime = this.state.lastTime;
      video.play();
    }
  }

  updateUnitSize() {
    const {
      bar,
    } = this.refs;

    if (bar.offsetWidth) {
      this.setState({
        barUnit: bar.offsetWidth / 100,
      });
    }
  }

  mouseDown(panStartX) {
    const {
      muted,
    } = this.state;

    if (muted) {
      this.setState({
        panStartX,
        volume: 0,
        muted: false,
      });
    } else {
      this.setState({
        panStartX,
      });
    }
  }

  mouseMove(x) {
    const {
      volume,
      panStartX,
      barUnit,
    } = this.state;

    if (!panStartX) return;

    const {
      pan,
      bar,
      video,
    } = this.refs;

    const startX = panStartX - (barUnit * volume) - 16;
    const tempX = Math.min(Math.max(x - startX - 32, -16), 284);
    const tempVolume = (tempX + 16) / barUnit;

    let barStyle = `linear-gradient(to right, #fbbb0c 0, #fbbb0c ${tempVolume}%`;
    barStyle += `, transparent ${tempVolume}%, transparent 100%)`;

    bar.style.backgroundImage = barStyle;
    pan.style.left = `${tempX}px`;
    video.volume = tempVolume / 100;
  }

  mouseUp() {
    const {
      panStartX,
    } = this.state;

    if (!panStartX) return;

    const {
      pan,
    } = this.refs;

    const nowX = parseInt(pan.style.left.replace(/px$/, ''), 10);
    const volume = (nowX + 16) / 3;

    this.setState({
      panStartX: null,
      volume,
      muted: !volume,
    });
  }

  togglePlayState() {
    const {
      video,
    } = this.refs;

    if (this.state.playing) {
      video.pause();
      window.removeEventListener('ended', this.videoListener);
    } else {
      this.videoListener = () => this.videoEnd();
      video.addEventListener('ended', this.videoListener);
      video.play();
    }

    this.setState({
      playing: !this.state.playing,
    });
  }

  toggleMuteState() {
    const {
      video,
    } = this.refs;

    if (this.state.muted) {
      video.muted = false;
    } else {
      video.muted = true;
    }

    this.setState({
      muted: !this.state.muted,
    });
  }

  refreshPlayState() {
    const {
      video,
    } = this.refs;

    video.play();
    video.currentTime = 0;

    this.setState({
      playing: true,
    });
  }

  videoOn() {
    const {
      video,
    } = this.refs;

    const {
      mode,
    } = this.state;

    if (mode === ON) return;

    this.setState({
      playing: true,
      mode: ON,
      lastTime: video.currentTime,
    });
  }

  videoOff() {
    const {
      video,
    } = this.refs;

    const {
      mode,
    } = this.state;

    if (mode === OFF) return;

    this.setState({
      playing: true,
      mode: OFF,
      lastTime: video.currentTime,
    });
  }

  videoEnd() {
    window.removeEventListener('ended', this.videoListener);
    this.setState({
      playing: false,
    });
  }

  render() {
    const {
      list,
      params,
    } = this.props;

    const {
      mode,
      playing,
      volume,
      muted,
      barUnit,
    } = this.state;

    const activedSpec = list.find(s => s.key === params.specKey);
    if (!activedSpec) return <div style={styles.wrap}></div>;

    const actualVolume = muted ? 0 : volume;
    let barStyle = `linear-gradient(to right, #fbbb0c 0, #fbbb0c ${actualVolume}%`;
    barStyle += `, transparent ${actualVolume}%, transparent 100%)`;

    return (
      <div style={styles.wrap} onMouseUp={() => this.mouseUp()} onTouchEnd={() => this.mouseUp()}>
        <h2 style={styles.title}>{activedSpec.name}</h2>
        <video style={styles.video} ref="video" src={(mode === ON ? activedSpec.videoOn : activedSpec.videoOff)} />
        <div style={styles.controller}>
          <button type="button" style={styles.controlBtn} onClick={() => this.togglePlayState()} key="play">
            <span className={`fa ${playing ? 'fa-pause' : 'fa-play'}`} />
          </button>
          <button
            type="button"
            style={styles.controlBtn}
            onClick={() => this.refreshPlayState()}
            key="refresh">
            <span className="fa fa-refresh" />
          </button>
          <button type="button" style={styles.controlBtn} onClick={() => this.toggleMuteState()} key="mute">
            <span className={`fa ${muted ? 'fa-volume-off' : 'fa-volume-up'}`} />
          </button>
          <div
            ref="bar"
            onMouseMove={e => this.mouseMove(e.clientX)}
            onTouchMove={e => this.mouseMove(e.changedTouches[0].clientX)}
            style={[styles.progressBar, { backgroundImage: barStyle }]}>
            <button
              type="button"
              ref="pan"
              onMouseDown={e => this.mouseDown(e.clientX)}
              onTouchStart={e => this.mouseDown(e.changedTouches[0].clientX)}
              style={[styles.progressPan, { left: barUnit * actualVolume - 16 }]} />
          </div>
        </div>
        <div style={styles.movieMode}>
          <button
            type="button"
            style={styles.movieModeToggle}
            onClick={() => this.videoOn()}>啟用</button>
          <button
            type="button"
            style={[styles.movieModeToggle, styles.reverseColor]}
            onClick={() => this.videoOff()}>不啟用</button>
        </div>
      </div>
    );
  }
}

SpecificationViewer.propTypes = {
  list: T.array,
  params: T.shape({
    specKey: T.string,
  }),
};

export default connect(
  state => ({
    list: state.Specification.list,
  }),
)(radium(SpecificationViewer));



// WEBPACK FOOTER //
// ./containers/Specifications/SpecificationViewer.js